import React, { useMemo } from 'react';
import CookbookFilteredMealListContainer from './CookbookFilteredMealListContainer';
import {
  useTimestamp,
  AddFavoriteCookbookMealConnectorApollo,
  RemoveFavoriteCookbookMealConnectorApollo,
  CookbookMealListConnectorApollo,
  CookbookMealListEdge,
} from '@front-activerse/common-components';
import { useLocation } from 'react-router-dom';

/**
 * The filter screen passes only the ids of the matching meals. Their data is read from the meal
 * list query — already cached by the filter screen — so favourite changes made here show up.
 */
const useFilteredMeals = (meals: CookbookMealListEdge[] | undefined, mealIds: number[]) =>
  useMemo(() => {
    if (!meals) {
      return undefined;
    }

    const mealIdSet = new Set(mealIds);

    return meals.filter(({ node }) => mealIdSet.has(node.id));
  }, [meals, mealIds]);

const FilteredMeals: React.FC<{
  meals: CookbookMealListEdge[] | undefined;
  loading: boolean;
  mealIds: number[];
}> = ({ meals, loading, mealIds }) => {
  const { timestamp1 } = useTimestamp();
  const filteredMeals = useFilteredMeals(meals, mealIds);

  return (
    <AddFavoriteCookbookMealConnectorApollo>
      {({ addCookbookMealToFavorite }) => (
        <RemoveFavoriteCookbookMealConnectorApollo>
          {({ removeCookbookMealFromFavorites }) => (
            <CookbookFilteredMealListContainer
              loading={loading}
              meals={filteredMeals}
              addToFavorites={addCookbookMealToFavorite}
              removeFromFavorites={removeCookbookMealFromFavorites}
              key={timestamp1}
            />
          )}
        </RemoveFavoriteCookbookMealConnectorApollo>
      )}
    </AddFavoriteCookbookMealConnectorApollo>
  );
};

const EMPTY_MEAL_IDS: number[] = [];

export const CookbookFilteredMealListPage: React.FC = () => {
  const location = useLocation();
  const mealIds: number[] = location.state?.mealIds ?? EMPTY_MEAL_IDS;

  return (
    <CookbookMealListConnectorApollo>
      {({ meals, loading }) => <FilteredMeals meals={meals} loading={loading} mealIds={mealIds} />}
    </CookbookMealListConnectorApollo>
  );
};

export default CookbookFilteredMealListPage;
